// src/pages/Earnings.js — job history (riders on fixed monthly pay, no in-app earnings)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRider } from '../context/RiderContext';
import { getRiderHistory } from '../services/firebase';
import { FEATURES } from '../config/features';
import { useLanguage } from '../i18n/LanguageContext';
import { formatT } from '../i18n/translations';

export default function Earnings() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { rider } = useRider();
  const [period, setPeriod] = useState('all');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    if (!rider) return undefined;
    const unsub = getRiderHistory(rider.uid, (data) => {
      setHistory(data);
      setHistoryLoading(false);
    });
    return unsub;
  }, [rider]);

  const filterByPeriod = (items) => {
    const now = new Date();
    return items.filter((item) => {
      const date = item.completedAt?.toDate?.() || item.createdAt?.toDate?.() || new Date(item.createdAt || 0);
      if (period === 'today') return date.toDateString() === now.toDateString();
      if (period === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return date >= weekAgo;
      }
      if (period === 'month') {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return date >= monthAgo;
      }
      return true;
    });
  };

  const visibleHistory = FEATURES.RIDES_ENABLED ? history : history.filter((i) => i.type === 'delivery');
  const filtered = filterByPeriod(visibleHistory);

  const formatDate = (item) => {
    const date = item.completedAt?.toDate?.() || item.createdAt?.toDate?.() || new Date(item.createdAt || 0);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return t('history.today');
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      <div className="topbar">
        <button type="button" className="back-btn" onClick={() => navigate('/')}>←</button>
        <div style={{ fontWeight: 800, fontSize: 16, flex: 1 }}>{t('history.title')}</div>
      </div>

      <div className="page-content" style={{ paddingBottom: 80 }}>
        <div style={{ padding: '16px 16px 0' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto' }}>
            {[
              { id: 'today', label: t('history.today') },
              { id: 'week', label: t('history.week') },
              { id: 'month', label: t('history.month') },
              { id: 'all', label: t('history.all') },
            ].map((p) => (
              <button key={p.id} type="button" onClick={() => setPeriod(p.id)} style={{
                padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${period === p.id ? 'var(--green)' : 'var(--border)'}`,
                background: period === p.id ? 'rgba(0,214,143,0.1)' : 'var(--card)',
                color: period === p.id ? 'var(--green)' : 'var(--gray)',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                fontFamily: 'DM Sans, sans-serif',
              }}>{p.label}</button>
            ))}
          </div>

          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--gray)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t('history.empty')}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>{t('history.emptySub')}</div>
            </div>
          ) : (
            filtered.map((item) => (
              <div key={item.id} className="card" style={{ padding: 14, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0,214,143,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                  {item.type === 'delivery' || !FEATURES.RIDES_ENABLED ? '📦' : '🛺'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.type === 'delivery' || !FEATURES.RIDES_ENABLED
                      ? formatT(t('history.deliveryTo'), { address: item.deliveryAddress || 'Customer' })
                      : `→ ${item.destination?.name || 'Trip'}`}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                    {formatDate(item)} • {item.type === 'delivery' ? `${item.items?.length || 0} items` : (item.distance || '—')}
                  </div>
                  {item.riderRating && (
                    <div style={{ fontSize: 11, color: '#FFD700', marginTop: 2 }}>
                      {'⭐'.repeat(Math.round(item.riderRating))} {item.riderRating.toFixed(1)}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>{t('history.done')}</div>
                  <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 1 }}>
                    {item.paymentMode === 'cash' ? t('history.cash') : t('history.online')}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bottom-nav">
        {[
          { label: t('nav.home'), emoji: '🏠', path: '/' },
          { label: t('nav.history'), emoji: '📋', path: '/history', active: true },
          { label: t('nav.profile'), emoji: '👤', path: '/profile' },
        ].map((n) => (
          <button key={n.label} type="button" className={`nav-item ${n.active ? 'active' : ''}`} onClick={() => navigate(n.path)}>
            <div className="nav-icon">{n.emoji}</div>
            <div className="nav-label">{n.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
